import {
  get as _get,
  reduce as _reduce,
  map as _map,
  invertBy as _invertBy,
} from 'lodash';
import classNames from 'classnames/bind';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Row } from '@saucelabs/sl-component-grid';
import {
  sortVersions,
  sortResolutions,
  sortOSes,
  displayNamesMap,
  isMobileOS,
} from '@saucelabs/sl-util-helpers';

import Styles from './DesktopBrowserConfigurator.styles';
import DesktopBrowserConfiguratorOS from './DesktopBrowserConfiguratorOS';
import DesktopBrowserConfiguratorResolution from './DesktopBrowserConfiguratorResolution';
import DesktopBrowserConfiguratorTunnel from './DesktopBrowserConfiguratorTunnel';
import DesktopBrowserConfiguratorPlatform from './DesktopBrowserConfiguratorPlatform';
import DropdownColumn from './DropdownColumn';

const EXPERIMENTAL_VERSIONS = ['beta', 'dev'];
const isNotExperimentalVersion = (version) => !EXPERIMENTAL_VERSIONS.includes(version);

const prepareBrowsers = (browsers) => _reduce(browsers, (acc, value, key) => {
  // TODO move me to the utils
  // Opera is deprecated but the API is still returning the key in JSON
  if (!isMobileOS(key) && key !== 'opera') {
    acc[key] = value;
  }

  return acc;
}, {});

const getPlatformKey = (platform) => {
  const mapping = {
    microsoftedge: 'edge',
    'internet explorer': 'iexplore',
  };

  return mapping[platform.toLowerCase()] || platform;
};

class DesktopBrowserConfigurator extends Component {
  constructor(props) {
    super(props);

    this.state = {
      platforms: prepareBrowsers(props.browsers),
      oses: [],
      resolutions: [],

      platform: 'edge',
      version: null,
      os: null,
      resolution: null,
    };
  }

  componentWillMount() {
    const { platform } = this.state;
    const { browserConfiguration: { meta: { initial } } } = this.props;

    if (!initial) {
      return this.handleSelection({ platform });
    }

    return this.handleSelection({
      platform: initial.browser,
      version: initial.version,
      os: initial.device,
      resolution: initial.res,
    });
  }

  componentWillReceiveProps(nextProps) {
    const { browserConfiguration: { input: { value: currentValue } } } = this.props;
    const { browserConfiguration: { input: { value: nextValue } } } = nextProps;

    if (nextValue !== currentValue && nextValue.browser) {
      this.handleSelection({
        platform: nextValue.browser,
        version: nextValue.version,
        os: nextValue.device,
        resolution: nextValue.res,
      });
    }
  }

  handleSelection = (data = {}) => {
    const { platform, version, os, resolution, platforms } = this.state;
    const { browserConfiguration } = this.props;
    // TODO move this logic to manual form initialValues
    const selection = {
      version,
      os,
      resolution,
      ...data,
      platform: getPlatformKey(data.platform || platform), // always map the platform
    };

    if (!selection.version) {
      const versions = _get(platforms, [selection.platform], {});
      selection.versions = Object.keys(versions).sort(sortVersions);
      selection.version = selection.versions.find(isNotExperimentalVersion);
    }

    const oses = _get(platforms, [selection.platform, selection.version], {});
    const osVersionsMapping = _invertBy(oses, (osProps) => osProps.platform);
    const sortedOsVersions = Object.keys(osVersionsMapping).sort(sortOSes);
    selection.oses = sortedOsVersions.map((osVersion) => osVersionsMapping[osVersion][0]);

    if (!selection.os || !oses[selection.os]) {
      selection.os = selection.oses[0];
    }

    const resolutions = _get(platforms, [
      selection.platform,
      selection.version,
      selection.os,
      'resolutions',
    ], []);
    selection.resolutions = resolutions.sort(sortResolutions);

    if (!selection.resolution || !resolutions.includes(selection.resolution)) {
      selection.resolution = selection.resolutions[0];
    }

    const configuration = _get(platforms, [
      selection.platform,
      selection.version,
      selection.os,
    ], {});

    const onChangeObj = Object.assign({}, {
      browser: configuration.browser,
      browserDisplay: displayNamesMap[selection.platform],
      browserVersionDisplay: selection.version,
      device: selection.os,
      res: selection.resolution,
      os: configuration.platform,
      osDisplay: selection.os,
      osVersionDisplay: selection.os,
      version: selection.version,
    });

    browserConfiguration.input.onChange(onChangeObj);
    this.setState(selection);
  }

  renderPlatforms() {
    const { platform, platforms, resolution, resolutions, version } = this.state;
    const onChangeResolution = (newValue) => this.handleSelection({ resolution: newValue });

    const platformList = _map(platforms, (versions, platformName) => (
      <DesktopBrowserConfiguratorPlatform
        key={platformName}
        currentPlatform={platform}
        currentVersion={version}
        onChange={this.handleSelection}
        platformName={platformName}
        versions={versions}
      />
    ));

    return (
      <Row className={Styles.row}>
        {platformList}
        <DesktopBrowserConfiguratorResolution
          current={resolution}
          onChange={onChangeResolution}
          resolutions={resolutions}
        />
      </Row>
    );
  }

  render() {
    const { oses, os } = this.state;
    const { tunnels } = this.props;
    const onChangeOS = (newValue) => this.handleSelection({ os: newValue });

    return (
      <div className={classNames('form-group', Styles.default)}>
        <label htmlFor="url">
          Browser selection
        </label>
        {this.renderPlatforms()}
        <Row>
          <DropdownColumn key="os">
            <DesktopBrowserConfiguratorOS
              onChange={onChangeOS}
              os={os}
              oses={oses}
            />
          </DropdownColumn>
          <DropdownColumn key="tunnel">
            <DesktopBrowserConfiguratorTunnel tunnels={tunnels} />
          </DropdownColumn>
        </Row>
      </div>
    );
  }
}

DesktopBrowserConfigurator.propTypes = {
  browserConfiguration: PropTypes.object.isRequired,
  browsers: PropTypes.object.isRequired,
  tunnels: PropTypes.array.isRequired,
};

export default DesktopBrowserConfigurator;
